import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { WellnessAssessmentType } from '../../utils/types/wellness_types';
import { DASS21_QUESTIONS, SCALE_LABELS as DASS21_SCALE } from './Constants/DASS21_QUESTIONS';
import { GAD7_QUESTIONS, SCALE_LABELS as GAD7_SCALE } from './Constants/GAD7_QUESTIONS';
import { PHQ9_QUESTIONS, SCALE_LABELS as PHQ9_SCALE } from './Constants/PHQ9_QUESTIONS';
import { AssessmentShell } from './components/AssessmentShell';

// ─── Dummy API helpers (replace with real fetch when APIs are ready) ──────────

interface TokenValidation {
  valid: boolean;
  expired?: boolean;
  deadlineDate?: string;
  assessmentType?: WellnessAssessmentType;
  companyName?: string;
}

async function validateToken(token: string): Promise<TokenValidation> {
  // TODO: replace with real call: GET /wellness/assess/validate?token=
  void token;
  await new Promise((r) => setTimeout(r, 600)); // simulate network
  return {
    valid: true,
    assessmentType: 'DASS21',
    companyName: 'HiAcuity',
    deadlineDate: '2026-07-15',
  };
}

interface Answer { questionId: string; score: number }

async function submitAssessment(token: string, answers: Answer[]): Promise<void> {
  // TODO: replace with real call: POST /wellness/assess/submit
  void token; void answers;
  await new Promise((r) => setTimeout(r, 800)); // simulate network
}

// ─── Per-type question config ─────────────────────────────────────────────────

const ASSESSMENT_CONFIG: Record<WellnessAssessmentType, { questions: typeof DASS21_QUESTIONS; scaleLabels: string[] }> = {
  DASS21: { questions: DASS21_QUESTIONS, scaleLabels: DASS21_SCALE },
  GAD7:   { questions: GAD7_QUESTIONS,   scaleLabels: GAD7_SCALE },
  PHQ9:   { questions: PHQ9_QUESTIONS,   scaleLabels: PHQ9_SCALE },
};

// ─── Main page ────────────────────────────────────────────────────────────────

type Stage = 'loading' | 'invalid' | 'expired' | 'ready';

export const WellnessAssessmentPage: React.FC = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') ?? '';

  const [stage, setStage] = useState<Stage>('loading');
  const [tokenData, setTokenData] = useState<TokenValidation | null>(null);

  useEffect(() => {
    if (!token) { setStage('invalid'); return; }
    validateToken(token).then((data) => {
      setTokenData(data);
      if (!data.valid) setStage('invalid');
      else if (data.expired) setStage('expired');
      else setStage('ready');
    }).catch(() => setStage('invalid'));
  }, [token]);

  if (stage === 'loading') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50'>
        <div className='flex flex-col items-center gap-3'>
          <svg className='h-8 w-8 animate-spin text-emerald-500' fill='none' viewBox='0 0 24 24'>
            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
          </svg>
          <p className='text-sm text-slate-500'>Loading your assessment…</p>
        </div>
      </div>
    );
  }

  if (stage === 'invalid') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 px-4'>
        <div className='max-w-sm text-center'>
          <div className='mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-100'>
            <svg className='h-7 w-7 text-rose-500' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z' />
            </svg>
          </div>
          <h2 className='mb-2 text-base font-extrabold text-slate-900'>Invalid Link</h2>
          <p className='text-sm text-slate-600'>
            This wellness assessment link is invalid or has already been used. Please contact the recruiter if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  if (stage === 'expired') {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 px-4'>
        <div className='max-w-sm text-center'>
          <div className='mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-100'>
            <svg className='h-7 w-7 text-amber-600' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          </div>
          <h2 className='mb-2 text-base font-extrabold text-slate-900'>Link Expired</h2>
          <p className='text-sm text-slate-600'>
            This assessment link expired on{' '}
            <span className='font-semibold'>
              { tokenData?.deadlineDate
                ? new Date(tokenData.deadlineDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : 'the deadline' }
            </span>
            . Please contact the recruiter to request a new invitation.
          </p>
        </div>
      </div>
    );
  }

  const assessmentType = tokenData?.assessmentType ?? 'DASS21';
  const config = ASSESSMENT_CONFIG[assessmentType];

  return (
    <AssessmentShell
        assessmentType={assessmentType}
        questions={config.questions}
        scaleLabels={config.scaleLabels}
        companyName={tokenData?.companyName}
        onSubmit={(answers) => submitAssessment(token, answers)}
    />
  );
};
